import type { AddressCountry } from './types';

/**
 * Abbreviations people type that are not the country's name, lower-cased.
 *
 * Not a list of the countries ArchiPro supports — that set lives in the imported place data
 * and reaches the front end through the `addressCountries` query. Real names are resolved
 * below without a table.
 */
const COUNTRY_NAME_ALIASES: Record<string, AddressCountry> = {
    usa: 'US',
    uk: 'GB',
    'great britain': 'GB',
    britain: 'GB',
};

/** An ISO 3166-1 alpha-2 code is two letters. */
const ISO_COUNTRY_CODE = /^[A-Z]{2}$/;

/**
 * Resolves a country code or name to an ISO code, or null when the value names no country.
 *
 * Deliberately not checked against a list of supported countries. A country becomes
 * addressable by importing its place data, so a list here would reject a country the server
 * already serves — which is what it did: an Irish address resolved to nothing, and every
 * caller then fell back to its default or to the site's own country.
 *
 * Names go through `Intl.DisplayNames` rather than a table, so a newly imported country
 * resolves without an addition here. That lookup is only ever a code lookup; anything that
 * has to match the database matches on the code, which is what every stored value holds.
 */
export const parseAddressCountry = (
    country?: string | null,
): AddressCountry | null => {
    const trimmed = country?.trim();
    if (!trimmed) {
        return null;
    }

    const lowerCased = trimmed.toLowerCase();
    // Before the code check, because two of the aliases are themselves two letters: "uk" is
    // the country people type and "UK" is a reserved code that is not the one addresses use.
    const alias = COUNTRY_NAME_ALIASES[lowerCased];
    if (alias) {
        return alias;
    }

    const asCode = trimmed.toUpperCase();
    if (ISO_COUNTRY_CODE.test(asCode)) {
        return asCode;
    }

    return codeForCountryName(lowerCased);
};

/**
 * Codes by lower-cased country name, built on the first name lookup and reused.
 *
 * Enumerating every two-letter pair and asking `Intl.DisplayNames` for each is what stands
 * in for a country table: it hands an unassigned code straight back, so a name only enters
 * the map when the code is a real country. 676 lookups, once, and only when a caller passes
 * a name rather than a code.
 *
 * The first code to claim a name keeps it. More than one code can carry the same name --
 * "France" is both `FR` and the withdrawn `FX`, "United Kingdom" both `GB` and the reserved
 * `UK` -- and going in alphabetical order makes the assigned one the winner in each pair.
 */
let nameToCode: Map<string, AddressCountry> | null = null;

const codeForCountryName = (lowerCasedName: string): AddressCountry | null => {
    if (!nameToCode) {
        nameToCode = new Map();

        try {
            const display = new Intl.DisplayNames(['en'], { type: 'region' });

            for (let first = 65; first <= 90; first++) {
                for (let second = 65; second <= 90; second++) {
                    const code = String.fromCharCode(first, second);
                    const name = display.of(code);
                    if (name && name !== code) {
                        const key = name.toLowerCase();
                        if (!nameToCode.has(key)) {
                            nameToCode.set(key, code);
                        }
                    }
                }
            }
        } catch {
            // Leaves the map empty, so names simply do not resolve. Callers already treat
            // that as "not a country we know", which is the safe answer.
        }
    }

    return nameToCode.get(lowerCasedName) ?? null;
};
